import useSWR from 'swr';

import { IDetailResponse, IPaginatedResponse } from 'utils/types';

import { buildQuery, fetcher } from './utils';

export interface ICustomApiList {
  id: number;
  body_params_keys: any[];
  state: string;
  module: {
    auth_type: string;
    endpoint: string;
    verb: string;
    content_type: string;
  };
  module_type: string;
  name: string;
  query_params_keys: any[];
  token: {
    auth_type: string;
    endpoint: string;
    verb: string;
    content_type: string;
    username: string;
    password: string;
    grant_type: string;
    token_key_in_response: string;
  };
}

export const useCustomApiList = (params: object) => {
  const { data, error, isLoading, mutate, isValidating } = useSWR<
    IPaginatedResponse<ICustomApiList>
  >(`/v4/dash/external_apis?${buildQuery(params)}`, fetcher);

  return { data: data?.data, meta: data?.meta, error, isLoading, mutate, isValidating };
};

export const useCustomApiConfigDetails = () => {
  const { data, error, isLoading, mutate, isValidating } = useSWR(
    '/v4/dash/external_apis/configuration_details',
    fetcher
  );

  return { data, error, isLoading, mutate, isValidating };
};

export const useExternalApi = (id?: number | string | null) => {
  const key = id ? `/v4/dash/external_apis/${id}` : null;
  const { data, error, isLoading, mutate, isValidating } = useSWR<IDetailResponse<ICustomApiList>>(
    key,
    fetcher
  );

  return { data: data?.data, error, isLoading, mutate, isValidating };
};

export const fetchExternalApiById = async (id: number | string) => {
  const response = (await fetcher(
    `/v4/dash/external_apis/${id}`
  )) as IDetailResponse<ICustomApiList>;

  return response.data;
};

const fetchExternalApisByIds = async (ids: Array<number | string>) => {
  const responses = await Promise.all(
    ids.map(
      id => fetcher(`/v4/dash/external_apis/${id}`) as Promise<IDetailResponse<ICustomApiList>>
    )
  );

  return responses.map(response => response.data);
};

export const useExternalApisByIds = (ids?: Array<number | string> | null) => {
  const shouldFetch = Boolean(ids?.length);
  const key = shouldFetch ? ['externalApisByIds', ...(ids as Array<number | string>)] : null;
  const { data, error, isLoading, mutate, isValidating } = useSWR<ICustomApiList[]>(key, () =>
    fetchExternalApisByIds(ids as Array<number | string>)
  );

  return { data, error, isLoading, mutate, isValidating };
};
